import React, { useState, useEffect } from 'react';
import { Button, Modal, Form, InputGroup } from 'react-bootstrap';
import Decimal from 'decimal.js'; // Ensure Decimal.js is installed

const PaymentModal = ({
  show,
  onHide,
  overallSum,
  onProcessPayment,
  isLoading,
}) => {
  const [amountGiven, setAmountGiven] = useState('');
  const [comment, setComment] = useState('');

  // Reset state when modal opens or overallSum changes
  useEffect(() => {
    if (show) {
      setAmountGiven('');
      setComment('');
    }
  }, [show, overallSum]);

  // A net-negative total means the register pays the customer out (more Pfand
  // returned than goods sold). The change helper doesn't apply in that case.
  const total = new Decimal(overallSum || 0);
  const isPayout = total.lessThan(0);

  // Rückgeld is just an optional helper: only computed/shown once the operator
  // actually types a "Gegebener Betrag". Empty input => no change displayed.
  const hasAmountGiven = amountGiven !== '';
  let change = null;
  if (hasAmountGiven) {
    try {
      change = new Decimal(amountGiven || 0).minus(total).toNumber();
    } catch (error) {
      change = null;
    }
  }

  const handleAmountGivenChange = (e) => {
    setAmountGiven(e.target.value);
  };

  const handleSubmitPayment = () => {
    // No amount-given validation: cash payments may skip the Rückgeld helper,
    // so the operator can always submit.
    onProcessPayment(comment.trim());
  };

  return (
    <Modal show={show} onHide={onHide} backdrop={'static'}>
      <Modal.Header closeButton>
        <Modal.Title>Verkauf abschließen</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form>
          <Form.Group className='mb-4'>
            <Form.Label htmlFor='overallSumInput' className='fs-5'>
              {isPayout ? 'Auszahlung (Pfandrückgabe)' : 'Zu zahlen'}
            </Form.Label>
            <InputGroup size='lg'>
              <Form.Control
                id='overallSumInput'
                type='text'
                value={(isPayout ? total.abs() : total).toFixed(2)}
                readOnly
                disabled
                className={`text-end fw-bold fs-3 ${isPayout ? 'text-danger' : ''}`}
              />
              <InputGroup.Text className='fs-4'>€</InputGroup.Text>
            </InputGroup>
            {isPayout && (
              <Form.Text className='text-danger'>
                Betrag an den Kunden auszahlen.
              </Form.Text>
            )}
          </Form.Group>

          {/* The Rückgeld helper only makes sense when the customer pays in. */}
          {!isPayout && (
            <>
              <Form.Group className='mb-3'>
                <Form.Label htmlFor='amountGivenInput'>
                  Gegebener Betrag <span className='text-muted'>(optional)</span>
                </Form.Label>
                <InputGroup>
                  <Form.Control
                    id='amountGivenInput'
                    type='number'
                    step='0.01' // Allow decimal input
                    min='0'
                    value={amountGiven}
                    onChange={handleAmountGivenChange}
                    onWheel={(e) => e.currentTarget.blur()} // Disable scrollwheel
                    onKeyDown={(e) => {
                      // Disable arrow up/down keys
                      if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                        e.preventDefault();
                      }
                    }}
                    className='text-end'
                    autoFocus // Focus on this input when modal opens
                  />
                  <InputGroup.Text>€</InputGroup.Text>
                </InputGroup>
              </Form.Group>

              <Form.Group className='mb-3'>
                <Form.Label htmlFor='changeInput'>Rückgeld</Form.Label>
                <InputGroup>
                  <Form.Control
                    id='changeInput'
                    type='text'
                    value={hasAmountGiven ? new Decimal(change).toFixed(2) : ''}
                    placeholder='—'
                    readOnly
                    disabled
                    className={`text-end ${
                      hasAmountGiven
                        ? new Decimal(change).greaterThanOrEqualTo(0)
                          ? 'text-success fw-bold'
                          : 'text-danger'
                        : ''
                    }`}
                  />
                  <InputGroup.Text>€</InputGroup.Text>
                </InputGroup>
              </Form.Group>
            </>
          )}

          <Form.Group className='mb-2'>
            <Form.Label htmlFor='commentInput'>
              Kommentar/Bemerkung <span className='text-muted'>(optional, für spätere Zuordnung etc.)</span>
            </Form.Label>
            <Form.Control
              id='commentInput'
              as='textarea'
              rows={2}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder='z. B. Hinweis zur Zahlung/Crew/Rechnung'
            />
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant='secondary' onClick={onHide} disabled={isLoading}>
          Abbrechen
        </Button>
        <Button
          variant='success'
          onClick={handleSubmitPayment}
          disabled={isLoading}
        >
          Bezahlung abschließen
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default PaymentModal;
